import React, { useEffect, useState, useMemo } from "react";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import usePostCommand from "../../../customHooks/POST_requests/usePostCommand";
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";
// COMPONENTS
import ActionCommandButton from "../Actions/components/ActionCommandButton";
import GlobalAlert2 from "../../home/GlobalAlert2";
// INTERFACES
import { RootState } from "../../../app/store";
import { ActionCommandOption } from "../../../interfaces/Bank.interface";
// CONSTANTS
import {
  resolveCommandEnum,
  resolveValidStateEnums,
  assertBoundCommand,
  UNBOUND_COMMAND_REASON,
  dispatchFloor,
  floorSelectionMissing,
  logUnresolvedMessageId,
} from "./actionButtonUtils";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { UuidT } from "../../../flatbuffers/generated/front-end/uuid";
// VERIFICATION
import useActionOutcome, { ActionVerification } from "./useActionOutcome";

export default function ActionButtonToggle({
  validStates,
  toggleValidStates,
  label,
  toggleLabel,
  messageId,
  toggleMessageId,
  command = null,
  toggleCommand = null,
  colorAction,
  colorToggleAction,
  carService,
  targetFloor = undefined,
  expectedResponse = null,
  expectedResponseTimeout = null,
  commandMessageId = null,
}: {
  validStates: Array<string | UuidT>;
  toggleValidStates: Array<string | UuidT>;
  label: string;
  toggleLabel: string;
  messageId: string | UuidT;
  toggleMessageId: string | UuidT;
  command?: ActionCommandOption | null;
  toggleCommand?: ActionCommandOption | null;
  colorAction: string;
  colorToggleAction: string;
  carService: number;
  /**
   * The floor this row's commands dispatch to. `undefined` for a command that
   * takes no floor (dispatches at 0, as before); `null` while a floor-taking
   * command is still waiting for the operator to pick one. See dispatchFloor.
   */
  targetFloor?: number | null;
} & ActionVerification) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [localCarService, setLocalCarService] = useState<number>(0);
  const [lastCommandSent, setLastCommandSent] = useState<string>("ON");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { triggerPostCommand, isLoadingPostCommand, isErrorPostCommand } = usePostCommand();
  const { messages } = useGetAllMessages();
  const reportOutcome = useActionOutcome({
    expectedResponse,
    expectedResponseTimeout,
    commandMessageId,
  });

  // ************************************************ */
  // MEMOIZED VALUES ******************************** */
  // null (not 0) when the message is unbound — enum 0 is a real Control
  // (CarCallLock), so a 0 fallback would mis-fire that command.
  //
  // The message catalog answers first; the row's own `command` option is the
  // fallback that rescues a command the generated schema catalog does not
  // declare (an adapter-only control write), which otherwise leaves this button
  // looking live while sending nothing. See resolveCommandEnum.
  const messageEnum = useMemo(
    () => resolveCommandEnum(messages, messageId, command),
    [messages, messageId, command],
  );

  const toggleMessageEnum = useMemo(
    () => resolveCommandEnum(messages, toggleMessageId, toggleCommand),
    [messages, toggleMessageId, toggleCommand],
  );

  const validStatesNumbers = useMemo(
    () => resolveValidStateEnums(messages, validStates),
    [messages, validStates],
  );

  const toggleValidStatesNumbers = useMemo(
    () => resolveValidStateEnums(messages, toggleValidStates),
    [messages, toggleValidStates],
  );

  // ************************************************ */
  // FUNCTIONS ************************************** */
  // Send the action's BOUND command, never a hardcoded Control value.
  // enumId 0 is a legitimate Control (CarCallLock), so the guard must be
  // a null check — a falsy check would silently drop that command.
  const onClickToggleOn = async () => {
    if (!assertBoundCommand(messageEnum, label, theme)) {
      reportOutcome(label, false, messageId);
      return;
    }
    setLastCommandSent("ON");
    const delivered = await triggerPostCommand({
      floor: dispatchFloor(targetFloor),
      command: messageEnum,
      liftType: LiftType[LiftType.ELEVATOR] as "ELEVATOR",
      active: true,
    });
    reportOutcome(label, delivered, messageId);
  };

  const onClickToggleOff = async () => {
    if (!assertBoundCommand(toggleMessageEnum, toggleLabel, theme)) {
      reportOutcome(toggleLabel, false, toggleMessageId ?? null);
      return;
    }
    setLastCommandSent("OFF");
    const delivered = await triggerPostCommand({
      floor: dispatchFloor(targetFloor),
      command: toggleMessageEnum,
      liftType: LiftType[LiftType.ELEVATOR] as "ELEVATOR",
      active: false,
    });
    reportOutcome(toggleLabel, delivered, toggleMessageId ?? null);
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    setLocalCarService(carService);
  }, [carService]);

  // Why a command resolves to nothing, named in the console the moment the row
  // mounts — before the operator clicks a button that looks live and sends
  // nothing. Silent when the id resolves, and once per id, so a re-render does
  // not spam. See diagnoseMessageId for the cases it distinguishes.
  useEffect(() => {
    if (messageEnum == null) logUnresolvedMessageId("primary", label, messages, messageId);
  }, [messageEnum, messages, messageId, label]);

  useEffect(() => {
    if (toggleMessageEnum == null)
      logUnresolvedMessageId("paired", toggleLabel, messages, toggleMessageId);
  }, [toggleMessageEnum, messages, toggleMessageId, toggleLabel]);

  // ************************************************ */
  // COMPONENT ************************************** */
  // The two commands are gated independently: `validStates` governs the primary
  // and `toggleValidStates` the secondary, exactly as before. There is no shared
  // disabled flag — a car already in the primary's target state must still be
  // able to run the secondary.
  const primaryBlocked = validStatesNumbers.includes(localCarService);
  const secondaryBlocked = toggleValidStatesNumbers.includes(localCarService);
  const unavailableReason = t(
    "monitoringActions.unavailableInCurrentState",
    "Unavailable in the current service mode",
  );

  // A floor-taking command must not dispatch until the operator picks a floor
  // on the row's strip; `targetFloor` is null for exactly as long as that is
  // true. The gate is VISIBLE — a disabled button naming its reason — rather
  // than a click that silently sends nothing, and rather than the position-0
  // dispatch it replaces, which aimed a positional command at a floor the
  // operator never chose.
  const awaitingFloor = floorSelectionMissing(targetFloor);
  const selectFloorReason = t(
    "monitoringActions.selectFloorFirst",
    "Select a floor for this command first",
  );
  // A function rather than a ternary at each call site: the floor gate outranks
  // the service-state gate, and both have to reach one tooltip slot without
  // nesting a conditional inside a conditional.
  const reasonFor = (stateBlocked: boolean, commandEnum: number | null): string | undefined => {
    // Ahead of the transient gates: a missing binding is a configuration
    // fault that no amount of waiting or floor-picking will clear, so it is
    // the reason worth showing.
    if (commandEnum == null) return UNBOUND_COMMAND_REASON;
    if (awaitingFloor) return selectFloorReason;
    if (stateBlocked) return unavailableReason;
    return undefined;
  };

  return (
    <>
      <ActionCommandButton
        label={label}
        color={colorAction}
        disabled={primaryBlocked || awaitingFloor || isLoadingPostCommand || messageEnum == null}
        loading={isLoadingPostCommand && lastCommandSent === "ON"}
        disabledReason={reasonFor(primaryBlocked, messageEnum)}
        onClick={onClickToggleOn}
      />
      {/* Always rendered. A TOGGLE is a two-command action by definition, so the
          secondary is not conditional on the paired command resolving: hiding it
          turned a misconfigured row into a row that merely looked like a
          different (single-command) type, which is the one thing an operator
          cannot report. An unbound command is caught at dispatch by
          assertBoundCommand, which names the action instead of no-op'ing. */}
      <ActionCommandButton
        label={toggleLabel}
        color={colorToggleAction}
        disabled={
          secondaryBlocked || awaitingFloor || isLoadingPostCommand || toggleMessageEnum == null
        }
        loading={isLoadingPostCommand && lastCommandSent === "OFF"}
        disabledReason={reasonFor(secondaryBlocked, toggleMessageEnum)}
        onClick={onClickToggleOff}
      />
      {/** ---------------------------------------------------- */}
      {/** GLOBAL ALERT */}
      <GlobalAlert2
        isError={isErrorPostCommand}
        isSuccess={false}
        requestType="POST"
        error="Error"
        name="Command"
      />
    </>
  );
}
